import { ReactElement, useCallback, useEffect, useMemo, useRef } from 'react';
import styles from './PlaybackButtons.module.scss';
import CustomIconButton from '@customer/components/inputs/custom-icon-button/CustomIconButton';
import {
  InvestigationEnitityIdProps,
  InvestigationPlaybackState,
  setCurrentTime,
  setIsPlaying,
  StepDirection,
  stepRawTrack,
  useInvestigationEntity,
} from '@customer/store/slices/investigationsSlice';
import { useAppDispatch } from '@customer/store/hooks';

interface UsePlaybackProps extends InvestigationEnitityIdProps {
  playback: InvestigationPlaybackState | undefined;
}

interface UsePlaybackReturn {
  changeIsPlaying: () => void;
}

const usePlayback = ({
  siteId,
  investigationId,
  playback,
}: UsePlaybackProps): UsePlaybackReturn => {
  const dispatch = useAppDispatch();

  const isPlaying = playback?.isPlaying ?? false;
  const isPlayingRef = useRef<boolean>(isPlaying);

  if (isPlayingRef.current !== isPlaying) {
    isPlayingRef.current = isPlaying;
  }

  const currentTimeRef = useRef<number>(playback?.currentTime ?? -1);

  if (
    typeof playback?.currentTime === 'number' &&
    currentTimeRef.current !== playback.currentTime
  ) {
    currentTimeRef.current = playback.currentTime;
  }

  /**
   * React Router fails to navigate while recursive effects are running.
   * React Router also cannot be trusted for up-to-date location accuracy while recursive effects are running.
   * There does not appear to be any documentation about this react-router limitation.
   * The solution is to directly examine the window.location and watch for changes.
   * If the window.location does change, the circuitbreaker ref can be used to interrupt the recursive effect.
   */
  const locationRef = useRef<string | null>(window.location.pathname);
  const circuitBreaker = useRef<boolean>(false);

  if (locationRef.current !== window.location.pathname) {
    locationRef.current = window.location.pathname;
    circuitBreaker.current = true;
  }

  const startTime = useRef<number>(-1);
  const referenceTime = useRef<number>(-1);
  const frameId = useRef<number | null>(null);

  /** This callback cancels the recursive continuePlayback function by canceling the animation frame. */
  const cancelPlaybackLoop = useCallback(() => {
    if (typeof frameId.current === 'number') {
      cancelAnimationFrame(frameId.current);
      frameId.current = null;

      // Handle the scenario where the component is unmounted before the user has paused playback.
      if (isPlayingRef.current) {
        // Reset the playback state to not playing.
        dispatch(setIsPlaying({ siteId, investigationId, isPlaying: false }));
      }
    }
  }, [dispatch, siteId, investigationId]);

  const continuePlaybackLoop = useCallback(() => {
    if (!circuitBreaker.current && isPlayingRef.current) {
      const time = Math.floor(performance.now());
      const elapsedTime = time - startTime.current;
      const newTime = referenceTime.current + elapsedTime;

      dispatch(setCurrentTime({ siteId, investigationId, currentTime: newTime }));

      // Continue the recursive playback loop and store the animation frame ID in the ref.
      frameId.current = requestAnimationFrame(continuePlaybackLoop);
    } else {
      cancelPlaybackLoop();
    }
  }, [dispatch, siteId, investigationId, cancelPlaybackLoop]);

  const startPlaybackLoop = useCallback(() => {
    if (currentTimeRef.current > -1) {
      referenceTime.current = currentTimeRef.current;
      startTime.current = Math.floor(performance.now());
      frameId.current = requestAnimationFrame(continuePlaybackLoop);
    }
  }, [continuePlaybackLoop]);

  useEffect(() => {
    // Only start the playback when there is no active animation loop and isPlaying has changed to true.
    if (frameId.current === null && isPlaying) {
      startPlaybackLoop();
    }

    // Effect cleanup.
    return () => {
      cancelPlaybackLoop();
    };
  }, [isPlaying, startPlaybackLoop, cancelPlaybackLoop]);

  const changeIsPlaying = useCallback(() => {
    dispatch(
      setIsPlaying({
        siteId,
        investigationId,
        isPlaying: !isPlayingRef.current,
      }),
    );
  }, [dispatch, siteId, investigationId]);

  return { changeIsPlaying };
};

export const PlaybackButtons = ({
  siteId,
  investigationId,
}: InvestigationEnitityIdProps): ReactElement => {
  const dispatch = useAppDispatch();

  const {
    currentRawTrack,
    playback,
    firstVisibleRawTrack: fv,
    lastVisibleRawTrack: lv,
    lastRawTrack,
  } = useInvestigationEntity({ siteId, investigationId });

  const { changeIsPlaying } = usePlayback({
    siteId,
    investigationId,
    playback,
  });

  const [disablePrev, disableNext, disablePlay] = useMemo<[boolean, boolean, boolean]>(() => {
    const isFirstVisibleTrack =
      typeof currentRawTrack?.id === 'number' ? currentRawTrack.id === fv?.id : false;
    const isLastVisibleTrack =
      typeof currentRawTrack?.id === 'number' ? currentRawTrack.id === lv?.id : false;

    const disablePlaybackButtons =
      typeof playback?.currentTime === 'undefined' || typeof lastRawTrack?.time === 'undefined';

    const disablePrev =
      disablePlaybackButtons ||
      typeof fv?.time === 'undefined' ||
      isFirstVisibleTrack ||
      playback.currentTime < fv.time;
    const disableNext =
      disablePlaybackButtons ||
      typeof lv?.time === 'undefined' ||
      isLastVisibleTrack ||
      playback.currentTime > lv.time;
    const disablePlay = disablePlaybackButtons || playback.currentTime >= lastRawTrack?.time;

    return [disablePrev, disableNext, disablePlay];
  }, [
    currentRawTrack?.id,
    fv?.id,
    fv?.time,
    lv?.id,
    lv?.time,
    playback?.currentTime,
    lastRawTrack?.time,
  ]);

  const onStepRawTrack = useCallback(
    (direction: StepDirection) => {
      dispatch(stepRawTrack({ siteId, investigationId, direction }));
    },
    [dispatch, siteId, investigationId],
  );

  const onStepNext = useCallback(() => {
    onStepRawTrack(1);
  }, [onStepRawTrack]);
  const onStepPrev = useCallback(() => {
    onStepRawTrack(-1);
  }, [onStepRawTrack]);

  return (
    <div className={styles.buttons}>
      <CustomIconButton svgIcon="go-to-first-page" onClick={onStepPrev} disabled={disablePrev} />

      <CustomIconButton
        svgIcon={playback?.isPlaying ? 'pause' : 'play'}
        size="large"
        onClick={changeIsPlaying}
        disabled={disablePlay}
      />

      <CustomIconButton svgIcon="go-to-last-page" onClick={onStepNext} disabled={disableNext} />
    </div>
  );
};

export default PlaybackButtons;
